<template>
    <section class="table-responsive-sm p-0 m-0" style="justify-content: stretch;">

        <div class="table_wrapper">
            <table :class="`table table-${theme}
                ${(borderless) ? 'table-borderless' : ''}
                ${(bordered) ? 'table-bordered' : ''}
                ${(striped) ? 'table-striped' : ''}
                ${(hover) ? 'table-hover' : ''}
                ${(small) ? 'table-sm' : ''}`">

                <thead>
                    <tr :class="`text-${header_text_color}`">

                        <template v-for="(header, header_index) in get_headers" :key="`${header_index}_header`">
                            <th :class="`table__header ${(theme !== 'light' ? `bg-${theme} text-dark` : '')} px-3 py-2`"
                                v-if="checkHeader(header)">
                                    <span class="description-xxs poppins-bold text-capitalize">
                                        {{ findLabel(header) }}
                                    </span>
                            </th>
                        </template>

                        <!-- Missing Headers -->
                        <template v-for="(m_header, index_m_header) in missing_headers" :key="`${index_m_header}_missing_header`">
                            <th :class="`table__header ${(theme !== 'light' ? `bg-${theme} text-white` : '')} px3 py-2`">
                                <span class="description-xxs poppins-bold text-capitalize">
                                    {{ findLabel(m_header) }}
                                </span>
                            </th>
                        </template>

                    </tr>
                </thead>

                <tbody v-if="data !== null">
                    <tr v-for="(row_item, row_index) in data" :key="row_index"
                        :class="`table__row p-1`">

                        <template v-for="(col, col_index) in row_item" :key="`${col_index}_item`">
                            <td :class="`text-${body_text_color} bg-${body_color}`"
                                v-if="checkHeader(col_index)">

                                <slot :name="col_index"
                                    v-bind:value="row_item[col_index]"
                                    v-bind:row="row_item">
                                        <span class="description-xxs">
                                            {{ col }}
                                        </span>
                                </slot>

                            </td>
                        </template>

                        <template v-for="(col_missing, col_missing_index) in missing_headers" :key="`${col_missing_index}_missing_item`">
                            <td :class="`text-${body_text_color} bg-${body_color}`">

                                <slot :name="col_missing"
                                    v-bind:row="row_item">
                                        <span class="description-xxs">
                                            {{ col_missing }}
                                        </span>
                                </slot>

                            </td>
                        </template>
                    </tr>
                </tbody>
            </table>

            <div class="w-100 flex-center py-4">
                <span class="description-xxs" v-if="data == null || data.length == 0">
                    No se encontraron registros
                </span>
            </div>
        </div>


        <div v-if="pagination">

            <div class="col-12 d-flex justify-content-between bg-gray p-3 py-4 rounded-3">
                <div class="flex-center">
                    <span class="description-xxs text-dark">Registros por p&aacute;gina: </span>

                    <div class="flex-center">
                        <select v-model="local_items_per_page"
                            @change="changeItemsPerPage()"
                            class="form-control select-items description-xxs text-center text-dark border border-warning ms-2"
                            style="width: 38px !important; height: 28px;">

                            <option class="p-0" value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                </div>

                <div class="flex-center">
                    <button @click="previousPage()"
                        class="btn btn-dark btn-circle flex-center"
                        :class="[(current_page <= 1) ? '' : 'back-and-foward']"
                        :disabled="current_page <= 1"
                        style="width: 22px; height: 22px;">
                            <img :src="arrow_left" height="18" width="18">
                    </button>

                    <section class="flex-center ms-2">
                        <input v-model="local_current_page"
                            @keydown.enter="changePage()"
                            class="form-control description-xxs me-1 text-center text-dark border border-warning"
                            style="width: 38px !important; height: 28px;">
                    </section> <span class="me-2">/ {{ total_pages }}</span>


                    <button @click="nextPage()"
                        class="btn btn-dark btn-circle flex-center"
                        :class="[(current_page >= total_pages) ? '' : 'back-and-foward']"
                        :disabled="current_page >= total_pages"
                        style="width: 22px; height: 22px;">
                            <img :src="arrow_right" height="18" width="18">
                    </button>

                    <span class="description-xxs text-white"> de {{ total_pages }}</span>
                </div>
            </div>
        </div>
    </section>

</template>

<script setup>
    import { ref, computed, watch, onMounted } from 'vue'
    import { useRouter } from 'vue-router'
    import { useStore } from "vuex"

    // Images
    import arrow_left from 'ASSETS/icons/table/chevron_left.svg'
    import arrow_right from 'ASSETS/icons/table/chevron_right.svg'

    // Hooks
    const router = useRouter()
    const store = useStore()

    // Props
    const props = defineProps({
        allow_headers: {
            type: Array,
            required: true,
            default: []
        },
        data: {
            type: [null, Array],
            required: true,
            default: []
        },
        theme: {
            type: String,
            required: false,
            default: 'light',
            validator: function (value) {
                return ['primary','secondary','success','warning', 'danger', 'info', 'light', 'dark'].includes(value)
            }
        },
        header_text_color: {
            type: String,
            required: false,
            default: 'dark',
            validator: function (value) {
                return ['primary','secondary','success','warning', 'danger', 'info', 'light', 'dark', 'white'].includes(value)
            }
        },
        body_text_color: {
            type: String,
            required: false,
            default: 'dark',
            validator: function (value) {
                return ['primary','secondary','success','warning', 'danger', 'info', 'light', 'dark', 'white'].includes(value)
            }
        },
        body_color:  {
            type: String,
            required: false,
            default: 'white',
            validator: function (value) {
                return ['primary','secondary','success','warning', 'danger', 'info', 'light', 'dark', 'white', 'transparent'].includes(value)
            }
        },
        borderless: {
            type: Boolean,
            required: false,
            default: false
        },
        bordered: {
            type: Boolean,
            required: false,
            default: false
        },
        striped: {
            type: Boolean,
            required: false,
            default: false
        },
        hover: {
            type: Boolean,
            required: false,
            default: true
        },
        small: {
            type: Boolean,
            required: false,
            default: true
        },
        pagination: {
            type: Boolean,
            required: false,
            default: false
        },
        current_page: {
            type: Number,
            required: false,
            default: 1
        },
        total_pages: {
            type: Number,
            required: false,
            default: 1
        },
        items_per_page: {
            type: Number,
            required: false,
            default: 10
        },
    })

    // Emits
    const emits = defineEmits(['change_items_per_page', 'previous_page', 'next_page', 'change_page'])

    // Variables
    let local_data = null
    let local_items_per_page = ref(null)
    let local_current_page = ref(null)

    // Injects

    // Variables

    // Watch
    watch(() => props.current_page, (first, second) => {
        local_current_page.value = props.current_page
    })

    // Computeds
    const get_headers = computed(() => {
        if(props.data !== null && props.data.length > 0) {
            return Object.keys(props.data[0])
        }
        return []
    })
    const allow_key_headers = computed(() => {
        let allow = []

        for(let i in props.allow_headers) {
            if(isObject(props.allow_headers[i]) && props.allow_headers[i].key !== null ) {
                allow.push(props.allow_headers[i].key)
            }
            else {
                allow.push(props.allow_headers[i])
            }
        }
        return allow
    })
    const missing_headers = computed(() => {
        let difference = allow_key_headers.value.filter(x => !get_headers.value.includes(x))
        return difference
    })
    

    // Methods
    // Methods For headers
    const checkHeader = (value) => {
        return value !== null && allow_key_headers.value.includes(value)
    }
    const findLabel = (key) => {
        for(let i in props.allow_headers) {
            let item = props.allow_headers[i]

            if(isObject(item) && item.key !== undefined && item.key !== null) {
                if(item.key == key) {
                    return item.label
                }
            }
            else {
                if(item == key) {
                    return item
                }
            }
        }
    }

    const isObject = (value) => {
        return typeof(value) === 'object' && !Array.isArray(value) && value !== null
    }


    // Methods For Emits
    const changeItemsPerPage = (event) => {
        emits('change_items_per_page', { items_per_page: local_items_per_page.value, page: 1 })
    }

    const previousPage = () => {
        if(props.current_page > 1) {
            emits('previous_page', { previous_page: props.current_page - 1 })
        }
        else {
            emits('previous_page', { previous_page: 1 })
        }
    }

    const changePage = () => {
        if(/^\d+$/.test(local_current_page.value)) {
            if(local_current_page.value >= 1 && local_current_page.value <= props.total_pages) {
                emits('change_page', { new_page: local_current_page.value })
            }
            else {
                local_current_page.value = props.current_page
            }
        }
        else {
            local_current_page.value = props.current_page
        }
    }

    const nextPage = () => {
        if(props.current_page < props.total_pages) {
            emits('next_page', { next_page: props.current_page + 1 })
        }
        else {
            emits('next_page', { next_page: props.total_pages })
        }
    }

    local_items_per_page.value = props.items_per_page
    local_current_page.value = props.current_page
</script>

<style lang="scss" scoped>
    @import 'ASSETS/scss/minxin.scss';

    .table-responsive-sm {
        overflow-y: unset !important;
        display: flex;
        flex-direction: column;
        justify-content: stretch;
    }

    .table_wrapper {
        overflow-y: scroll;
        scrollbar-width: none;
        height: 65vh;

        @include downPixels(576px) {
            height: 20vh;
        }

        @include downPixels(1440px) {
            height: 60vh;
        }

    }

    .table {
        padding: 0px;
        margin: 0px;
        // border-collapse: separate;
        // border-spacing: 0px 4px;

        th {
            position: sticky;
            top: 0px;
            z-index: 1;
        }


        &__header {
            vertical-align: middle;
            text-align: center;
            height: 20px;

            &:first-child {
                border-top-left-radius: 6px;
                // border-bottom-left-radius: 10px;
            }
            &:last-child {
                border-top-right-radius: 6px;
                // border-bottom-right-radius: 10px;
            }
        }

        &__row {
            vertical-align: middle;
            text-align: center;
            // height: 30px;
            // margin: 5px;
        }
    }


    .select-items {
        appearance: none;
        background-image: none;
        background-color: #fff;
        padding: 2px 0px;
    }

    .back-and-foward {

        &:hover {
            animation: back-and-foward 1.5s 1 backwards;
        }

    }


    @keyframes back-and-foward {
        0% {
            translate: 0px;
        }

        25% {
            translate: 2px;
        }

        50% {
            translate: -2px;
        }

        75% {
            translate: 2px;
        }

        100% {
            translate: 0px;
        }
    }

    .btn-circle {
        border-radius: 50%;
        width: 60px;       
        height: 60px;      
        padding: 12px;     
        text-align: center;
        font-size: 16px;   
    }

</style>